import { type DefaultError, useMutation, useQueryClient } from '@tanstack/react-query';
import { Undo2 } from 'lucide-react';
import { type AddAssetsResponse, type RemoveAssetsResponse, type ReorderAssetsParams } from '@/types/album';
import { addAlbumAssets, removeAlbumAssets, reorderAlbumAssets } from '@/services/album.service';
import { useGallerySelection } from '@/context/GallerySelectionContext';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { useBrowserStore } from '@/hooks/data/browser/useBrowserStore';
import { useCommandContext } from '@/context/CommandContext';
import { useToast } from '@/components/ui/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { QueryKey } from '@/lib/queryKeys';
import { useTranslations } from 'next-intl';

export const useAlbumAssets = () => {
  const queryClient = useQueryClient();
  const tToasts = useTranslations('toasts');
  const tCommon = useTranslations('common');

  const { toast } = useToast();
  const { setIsAllSelected } = useBrowserStore();
  const { deselectAllItems } = useGallerySelection();
  const { undo } = useCommandContext();

  const addAlbumAssetsQueryFn = useAuthenticatedQueryFn(addAlbumAssets);
  const removeAlbumAssetsQueryFn = useAuthenticatedQueryFn(removeAlbumAssets);
  const reorderAlbumAssetsQueryFn = useAuthenticatedQueryFn(reorderAlbumAssets);

  const { mutateAsync: addAssetsMutation } = useMutation<
    AddAssetsResponse,
    DefaultError,
    Parameters<typeof addAlbumAssets>['0']
  >({
    mutationFn: (params) => addAlbumAssetsQueryFn(params),
    onSuccess: (response, params) => {
      const assetsAdded = response.add ?? params!.assetIds;
      const addedCount = assetsAdded.length;
      toast({
        title: tToasts('addAssetsMutation.title', { count: addedCount }),
        description: tToasts('addAssetsMutation.description', { count: addedCount }),
        ...(params!.undoable && {
          action:
            addedCount > 0 ? (
              <ToastAction onClick={undo} altText={tToasts('addAssetsMutation.undoAlt')} icon={Undo2}>
                {tCommon('undo')}
              </ToastAction>
            ) : undefined,
        }),
      });
    },
    onSettled: () => {
      deselectAllItems();
      setIsAllSelected(false);

      void queryClient.invalidateQueries({ queryKey: [QueryKey.AssetData] });
    },
  });

  const { mutateAsync: removeAssetsMutation } = useMutation<
    RemoveAssetsResponse,
    DefaultError,
    Parameters<typeof removeAlbumAssets>['0']
  >({
    mutationFn: (params) => removeAlbumAssetsQueryFn(params),
    onSettled: (response, error, params) => {
      const removedCount = response?.remove?.length;
      if (removedCount) {
        toast({
          title: tToasts('removeAssetsMutation.title', { count: removedCount }),
          description: tToasts('removeAssetsMutation.description', {
            removedCount,
            totalCount: params!.assetIds.length,
          }),
          ...(params!.undoable && {
            action: (
              <ToastAction onClick={undo} altText={tToasts('removeAssetsMutation.undoAlt')} icon={Undo2}>
                {tCommon('undo')}
              </ToastAction>
            ),
          }),
        });
      }
      void queryClient.invalidateQueries({ queryKey: [QueryKey.AssetData] });
    },
  });

  const { mutateAsync: reorderAssetsMutation } = useMutation<
    ReorderAssetsParams,
    DefaultError,
    Parameters<typeof reorderAlbumAssets>['0']
  >({
    mutationFn: (params) => reorderAlbumAssetsQueryFn(params),
    onSettled: (response, error, params) => {
      const count = params!.assetIds.length;
      toast({
        title: tToasts('reorderAssetsMutation.title', { count }),
        description: tToasts('reorderAssetsMutation.description', { count }),
        ...(params!.undoable && {
          action: (
            <ToastAction onClick={undo} altText={tToasts('reorderAssetsMutation.undoAlt')} icon={Undo2}>
              {tCommon('undo')}
            </ToastAction>
          ),
        }),
      });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.AssetData] });
    },
  });

  return {
    addAssetsMutation,
    removeAssetsMutation,
    reorderAssetsMutation,
  };
};
